import { initializeApp, FirebaseApp } from "firebase/app";
import { getFirestore, Firestore } from "firebase/firestore";
import { getAnalytics, Analytics } from "firebase/analytics";
import { getAuth, Auth } from "firebase/auth";
import {
  getStorage as getFirebaseStorageSDK,
  FirebaseStorage,
} from "firebase/storage";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  AppCheck,
  getToken,
} from "firebase/app-check";

function getFirebaseConfig() {
  const config = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  };

  // Validate configuration
  if (!config.projectId) {
    throw new Error(
      "Firebase configuration missing. Please check your environment variables.",
    );
  }

  return config;
}

// Lazy initialization singletons
let app: FirebaseApp | null = null;
let firestore: Firestore | null = null;
let analytics: Analytics | null = null;
let auth: Auth | null = null;
let storage: FirebaseStorage | null = null;
let appCheck: AppCheck | null = null;

// Lazy initialize Firebase app
function getFirebaseApp(): FirebaseApp {
  if (!app) {
    app = initializeApp(getFirebaseConfig());
  }
  return app;
}

/**
 * Initialize App Check - MUST be called after successful login
 * and before using Firestore/Storage when App Check enforcement is enabled.
 * This ensures Firebase SDK requests include App Check tokens.
 */
export function initializeAppCheckAfterLogin(): void {
  if (appCheck) return; // Already initialized

  const siteKey =
    import.meta.env.VITE_RECAPTCHA_ENTERPRISE_SITE_KEY ||
    import.meta.env.VITE_RECAPTCHA_SITE_KEY;

  if (!siteKey) {
    console.warn("App Check site key not configured - skipping App Check initialization");
    return;
  }

  // Local development: reCAPTCHA does not issue valid tokens on localhost, so
  // use a Firebase App Check debug token instead (registered once per
  // developer in the Firebase console). Never active in production builds.
  if (import.meta.env.DEV && import.meta.env.VITE_APPCHECK_DEBUG_TOKEN) {
    globalThis.FIREBASE_APPCHECK_DEBUG_TOKEN =
      import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;
  }

  try {
    appCheck = initializeAppCheck(getFirebaseApp(), {
      provider: new ReCaptchaEnterpriseProvider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });
    console.info("✅ App Check initialized successfully");
  } catch (error) {
    console.error("Failed to initialize App Check:", error);
  }
}

// Lazy initialize Firestore
export function getDb(): Firestore {
  if (!firestore) {
    firestore = getFirestore(
      getFirebaseApp(),
      import.meta.env.VITE_FIRESTORE_DATABASE || "(default)",
    );
  }
  return firestore;
}

// Lazy initialize Analytics (only in browser environment)
export function getFirebaseAnalytics(): Analytics | null {
  if (typeof window === "undefined") {
    return null;
  }

  if (!analytics) {
    analytics = getAnalytics(getFirebaseApp());
  }
  return analytics;
}

// Lazy initialize Auth
export function getFirebaseAuth(): Auth {
  if (!auth) {
    auth = getAuth(getFirebaseApp());
  }
  return auth;
}

// Lazy initialize Storage
export function getFirebaseStorage(): FirebaseStorage {
  if (!storage) {
    storage = getFirebaseStorageSDK(getFirebaseApp());
  }
  return storage;
}

// Get App Check instance if initialized (returns null if not ready)
export function getAppCheckInstance(): AppCheck | null {
  return appCheck;
}

// Get App Check token for API requests
export async function getAppCheckToken(): Promise<string | null> {
  // Return null if App Check is not initialized yet
  if (!appCheck) {
    return null;
  }

  try {
    const tokenResult = await getToken(appCheck, false);
    return tokenResult.token;
  } catch (error) {
    console.error("Failed to get App Check token:", error);
    return null;
  }
}

// Export lazy getters instead of direct instances
export { getDb as db };
